// WorkspacesListTable.jsx
// Table component that lists workspaces and allows expanding rows to
// show per-workspace Pod details. This component is presentation-only
// and receives rendering helpers (status chip renderers) and action
// handlers from its parent.
import { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";

import {
  ACCENT,
  FONT,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  SURFACE_MUTED,
  HAIRLINE,
} from "../../../../theme/tokens";

import PodDetailsTable from "../components/PodDetailsTable";
import StatusPill from "../components/StatusPill";
import { WorkerResourceChips } from "../components/ResourceChips";
import { dataTableHeadSx } from "../../../../components/DataTableHeader.jsx";
import {
  formatTimestamp,
  formatDuration,
  EmptyState,
} from "../components/ViewUtils";

const buildPodSSHCommand = (statusDetails, pod) => {
  const gatewayInfo = statusDetails?.ssh_gateway_info;
  if (!gatewayInfo?.host || !gatewayInfo?.port) return "N/A";
  const statefulset = statusDetails?.statefulset;
  if (!statefulset) return "N/A";

  const userName = statefulset.user_account_name;
  const workspaceLabel = statefulset.user_label;
  const nodeIndex = pod.node_index;
  if (!userName || !workspaceLabel) return "N/A";

  const bastionUsername = `${userName}_${workspaceLabel}_${nodeIndex}`;
  return gatewayInfo.connection_command_template
    .replace("{port}", gatewayInfo.port)
    .replace("{username}", bastionUsername)
    .replace("{host}", gatewayInfo.host)
    .replace("{msg}", gatewayInfo.status_msg || "");
};

const getAggregatedStatefulSetStatus = (statefulset, pods) => {
  if (!statefulset) return { text: "No StatefulSet", color: "default" };
  if (!pods || pods.length === 0) {
    if (statefulset.deletion_timestamp)
      return { text: "Terminating", color: "warning" };
    return { text: "No Pods", color: "default" };
  }

  let hasRunning = false,
    hasPending = false,
    hasFailed = false,
    isTerminating = false;
  pods.forEach((pod) => {
    if (pod.deletion_timestamp) {
      isTerminating = true;
      return;
    }
    if (pod.phase === "Running") {
      const allReady = pod.container_statuses?.every((cs) => cs.ready);
      if (allReady) hasRunning = true;
      else hasPending = true;
    } else if (pod.phase === "Pending") hasPending = true;
    else if (pod.phase === "Failed") hasFailed = true;
  });

  if (isTerminating) return { text: "Terminating", color: "warning" };
  if (hasFailed) return { text: "Failed", color: "error" };
  if (hasPending) return { text: "Pending", color: "warning" };
  if (hasRunning) return { text: "Running", color: "success" };
  return { text: "Unknown", color: "default" };
};

const WorkspaceStatusChip = ({ workspace }) => {
  let statusDetails = workspace.status;
  let color = "default";
  let text = "Loading...";

  if (workspace.error) {
    text = "Error Fetching";
    color = "error";
  } else if (statusDetails) {
    const statefulset = statusDetails.statefulset;
    const pods = statusDetails.pods || [];
    const statusSummary = getAggregatedStatefulSetStatus(statefulset, pods);
    text = statusSummary.text;
    color = statusSummary.color;
  }
  return <StatusPill label={text} color={color} />;
};
WorkspaceStatusChip.propTypes = {
  workspace: PropTypes.object,
};

const ProgressCell = ({
  walltimeSeconds,
  remainingWalltimeSeconds,
  now,
  countdownAnchorMs,
}) => {
  if (walltimeSeconds == null || remainingWalltimeSeconds == null) {
    return (
      <Typography variant="body2" color="textSecondary">
        Initializing...
      </Typography>
    );
  }

  const elapsedSinceRender = Math.floor((now - countdownAnchorMs) / 1000);
  const remaining = Math.max(0, remainingWalltimeSeconds - elapsedSinceRender);
  const progress =
    walltimeSeconds > 0
      ? Math.min(
          100,
          Math.max(0, ((walltimeSeconds - remaining) / walltimeSeconds) * 100),
        )
      : 0;

  return (
    <Box sx={{ minWidth: 140 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
          Evicted in
        </Typography>
        <Typography
          sx={{ fontSize: FONT.xs, fontWeight: 600, color: INK_SECONDARY }}
        >
          {formatDuration(remaining)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 999,
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            backgroundColor: ACCENT,
          },
        }}
      />
    </Box>
  );
};
ProgressCell.propTypes = {
  walltimeSeconds: PropTypes.number,
  remainingWalltimeSeconds: PropTypes.number,
  now: PropTypes.number.isRequired,
  countdownAnchorMs: PropTypes.number.isRequired,
};

export default function WorkspacesListTable({
  workspaces = [],
  loading = false,
  expandedRows = {},
  onToggleExpand,
  onActionMenuOpen,
  onPodActionMenuOpen,
}) {
  const [countdownAnchorMs, setCountdownAnchorMs] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const nextNow = Date.now();
    setCountdownAnchorMs(nextNow);
    setNow(nextNow);
  }, [workspaces]);

  const hasActiveProgress = workspaces.some((workspace) => {
    const statefulset = workspace.status?.statefulset;
    return (
      statefulset?.walltime_seconds != null &&
      statefulset?.remaining_walltime_seconds != null
    );
  });

  useEffect(() => {
    if (!hasActiveProgress) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasActiveProgress]);

  return (
    <TableContainer
      sx={{ width: "100%", overflowX: "auto", mb: 0, boxShadow: "none" }}
    >
      <Table
        sx={{
          minWidth: 900,
          width: "100%",
          tableLayout: "fixed",
          ...dataTableHeadSx,
        }}
        aria-label="workspaces table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "4%" }} />
            <TableCell sx={{ width: "24%" }}>Workspace Name</TableCell>
            <TableCell sx={{ width: "10%" }}>Status</TableCell>
            <TableCell sx={{ width: "18%" }}>Resources</TableCell>
            <TableCell sx={{ width: "16%" }}>Created At</TableCell>
            <TableCell sx={{ width: "18%" }}>Progress</TableCell>
            <TableCell align="center" sx={{ width: "10%" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : workspaces && workspaces.length > 0 ? (
            workspaces.map((exp) => {
              const statusDetails = exp.status;
              const isExpanded = !!expandedRows[exp.workspace_id];
              const {
                creation_timestamp,
                walltime_seconds,
                remaining_walltime_seconds,
                user_label,
                cpu_limit_cores,
                mem_limit_gi,
                gpu_count,
                gpu_product,
              } = statusDetails?.statefulset || {};

              return (
                <Fragment key={exp.workspace_id}>
                  {/* Main table row */}
                  <TableRow sx={{ "& > *": { borderBottom: "unset" } }} hover>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => onToggleExpand?.(exp.workspace_id)}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Typography
                        sx={{
                          fontSize: FONT.md,
                          fontWeight: 600,
                          color: INK,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user_label || exp.workspace_name || "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: FONT.xs,
                          color: INK_MUTED,
                          fontFamily: "monospace",
                        }}
                      >
                        {exp.workspace_id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <WorkspaceStatusChip workspace={exp} />
                    </TableCell>

                    <TableCell>
                      <WorkerResourceChips
                        cpuCores={cpu_limit_cores}
                        memGib={mem_limit_gi}
                        gpuCount={gpu_count}
                        gpuProduct={gpu_product}
                        podCount={statusDetails?.pods?.length}
                      />
                    </TableCell>

                    <TableCell>{formatTimestamp(creation_timestamp)}</TableCell>

                    <TableCell>
                      <ProgressCell
                        walltimeSeconds={walltime_seconds}
                        remainingWalltimeSeconds={remaining_walltime_seconds}
                        now={now}
                        countdownAnchorMs={countdownAnchorMs}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        aria-label="actions"
                        size="small"
                        onClick={(event) =>
                          onActionMenuOpen?.(event, exp.workspace_id)
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expanded section: Pod details */}
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={7}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            my: 1,
                            p: 2,
                            backgroundColor: SURFACE_MUTED,
                            border: `1px solid ${HAIRLINE}`,
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            component="div"
                            sx={{
                              mb: 1.5,
                              fontSize: FONT.sm,
                              fontWeight: 600,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: INK_SECONDARY,
                            }}
                          >
                            Pod Details
                          </Typography>

                          {statusDetails?.pods &&
                          statusDetails.pods.length > 0 ? (
                            <PodDetailsTable
                              pods={statusDetails.pods.map((pod) => ({
                                ...pod,
                                sshCmd: buildPodSSHCommand(statusDetails, pod),
                              }))}
                              commandLabel="SSH Command"
                              onPodActionMenuOpen={onPodActionMenuOpen}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                              No pod information available or pods are still
                              provisioning.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={7} sx={{ borderBottom: "none" }}>
                <EmptyState
                  icon={ScienceOutlinedIcon}
                  title="No workspaces yet"
                  subtitle="Create an interactive workspace to start developing on the cluster."
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

WorkspacesListTable.propTypes = {
  workspaces: PropTypes.array,
  loading: PropTypes.bool,
  expandedRows: PropTypes.object,
  onToggleExpand: PropTypes.func,
  onActionMenuOpen: PropTypes.func,
  onPodActionMenuOpen: PropTypes.func,
};
